import React from 'react';
import { useTranslation } from 'react-i18next';
import { inputClass } from '../../pages/picture-ai/usePictureAi';

const normalizedLabel = (value) => `${value || ''}`.normalize('NFKC').replace(/\s+/gu, ' ').trim().toLocaleLowerCase('fr-FR');

/** A heuristic first guess, so an administrator edits a suggestion rather than an empty grid. */
const suggestedVariant = (label) => {
  const value = normalizedLabel(label);
  if (/étiquette|etiquette|prix|price/.test(value)) return 'price_label';
  if (/mobilier|meuble|plv/.test(value)) return 'furniture';
  if (/massification|mise en avant|tête de gondole|tete de gondole|mass display/.test(value)) return 'massification';
  if (/rayon.*(avant|après|apres)|(avant|après|apres).*rayon|shelf/.test(value)) return 'shelf_comparison';
  return 'generic';
};

/**
 * The extractor's per-question reading profile.
 *
 * A question missing from some of the configuration's programs is flagged here: without it, an
 * administrator targeting four programs would believe the agent runs on all four.
 */
const QuestionVariantMatrix = ({ value = {}, onChange, disabled, widgetProps = {}, questions = [] }) => {
  const { t } = useTranslation();
  const variants = widgetProps.variants || [];

  const overrideFor = (question) => value[question.key]
    || (question.legacy_keys || []).map(key => value[key]).find(Boolean)
    || null;

  const update = (question, patch) => {
    const existing = overrideFor(question) || { key: question.key, label: question.label, profile: suggestedVariant(question.label), instructions: '' };
    onChange({ ...value, [question.key]: { ...existing, key: question.key, label: question.label, ...patch } });
  };

  // Only the questions the configuration actually targets are listed: tuning a profile for a
  // question outside the scope would configure something that never runs.
  if (questions.length === 0) {
    return (
      <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
        {t('No targeted question yet — pick programs, then the questions this configuration should cover.')}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {questions.map((question) => {
        const override = overrideFor(question);
        return (
          <div key={question.key} className="grid gap-2 rounded-xl border border-gray-200 p-3 dark:border-gray-700 lg:grid-cols-[minmax(0,1fr)_14rem]">
            <div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">{question.label}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {t('{{count}} picture(s)', { count: question.pictures_count })}
                {question.ambiguous && ` · ${t('ambiguous label')}`}
                {question.missing_from?.length > 0 && (
                  <span className="ml-1 text-amber-600 dark:text-amber-400">
                    · {t('absent from {{programs}}', { programs: question.missing_from.join(', ') })}
                  </span>
                )}
              </div>
              <input
                className={`${inputClass} mt-2 text-xs`} disabled={disabled}
                placeholder={t('Extra instructions for this question (optional)')}
                value={override?.instructions || ''}
                onChange={event => update(question, { instructions: event.target.value })}
              />
            </div>
            <select
              className={inputClass} disabled={disabled}
              value={override?.profile || suggestedVariant(question.label)}
              onChange={event => update(question, { profile: event.target.value })}
            >
              {variants.map(variant => <option key={variant.value} value={variant.value}>{t(variant.label)}</option>)}
            </select>
          </div>
        );
      })}
    </div>
  );
};

export default QuestionVariantMatrix;
